import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useCreateDocument } from '../../../../modules/Documents/useCreateDocument';
import {
  AvailableTemplate,
  TabDocument,
  documentAuthor,
  documentLabel,
  initials,
  shortDate,
  statusLabel,
  statusTone,
} from '../../../../modules/Documents/config';
import { ProjectDetailReopen } from '../../config';
import PanelHeading from '../../../PanelHeading';
import StatusPill from '../../../StatusPill';
import { PlusIcon } from '../../../IconButton/icons';


interface DocumentsTabProps {
  projectId: number;
  stageId: number | null;
  isReadOnly: boolean;
  /** Close the modal before navigating to the full-screen editor. */
  onClose: () => void;
  /**
   * How to put this modal back, minus the tab. Travels with the navigation so
   * closing the editor returns here rather than to the documents register.
   */
  reopenIdentity: Omit<ProjectDetailReopen, 'tab'>;
}


const DocumentsTab: React.FC<DocumentsTabProps> = ({
  projectId,
  stageId,
  isReadOnly,
  onClose,
  reopenIdentity,
}) => {
  const supabase = useSupabaseClient();

  const [documents, setDocuments] = useState<TabDocument[]>([]);
  const [templates, setTemplates] = useState<AvailableTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Raising a document, naming it and opening it in the builder is shared with
  // Construction Admin -- see useCreateDocument.
  const {
    startCreate,
    openDocument,
    creatingTemplateId,
    dialog: createDialog,
    error: createError,
  } = useCreateDocument({ projectId, stageId, isReadOnly, onClose, reopenIdentity, returnTab: 'documents' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docsRes, templatesRes] = await Promise.all([
        supabase.rpc('get_project_documents', { p_project_id: projectId, p_stage_id: stageId }),
        supabase.rpc('get_available_document_templates', { p_project_id: projectId, p_stage_id: stageId }),
      ]);

      if (docsRes.error) throw docsRes.error;
      if (templatesRes.error) throw templatesRes.error;

      setDocuments((docsRes.data as TabDocument[]) || []);
      setTemplates((templatesRes.data as AvailableTemplate[]) || []);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Could not load documents for this ' + (stageId ? 'stage' : 'project') + '.');
    } finally {
      setLoading(false);
    }
  }, [supabase, projectId, stageId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group documents and templates under one heading per category, so a category
  // shows even when it only has a creatable template and no documents yet.
  const groups = useMemo(() => {
    const byCategory = new Map<string, { order: number; documents: TabDocument[]; templates: AvailableTemplate[] }>();

    const ensure = (category: string, order: number) => {
      const existing = byCategory.get(category);
      if (existing) {
        existing.order = Math.min(existing.order, order);
        return existing;
      }
      const created = { order, documents: [] as TabDocument[], templates: [] as AvailableTemplate[] };
      byCategory.set(category, created);
      return created;
    };

    templates.forEach(t => ensure(t.category, t.display_order).templates.push(t));
    documents.forEach(d => ensure(d.category, d.display_order).documents.push(d));

    return Array.from(byCategory.entries())
      .map(([category, g]) => ({ category, ...g }))
      .sort((a, b) => a.order - b.order || a.category.localeCompare(b.category));
  }, [documents, templates]);

  if (loading) {
    return (
      <div className="documents-tab">
        <p className="ui-table__empty">Loading documents…</p>
      </div>
    );
  }

  return (
    <div className="documents-tab">
      {error && !createError && <p className="field__error" role="alert">{error}</p>}
      {/* A certificate is created without the title prompt, so its failure has
          no dialog to sit in. */}
      {createError && !createDialog && <p className="field__error" role="alert">{createError}</p>}

      {/* Asked in its own dialog rather than inline: creating a document leaves
          this modal for the full-screen editor, so the title is the last thing
          the user is asked before the page changes under them. */}
      {createDialog}

      {groups.length === 0 && (
        <p className="ui-table__empty">
          No document types are available for this {stageId ? 'stage' : 'project'}.
        </p>
      )}

      {groups.map((group, index) => (
        <div key={group.category} className="documents-tab__group">
          {/* The heading names the type, so a lone template's button is just
              "+ New". Only when a category offers several templates does the
              button say which. */}
          <PanelHeading
            spaced={index > 0}
            title={group.category}
            meta={group.documents.length > 0 ? `${group.documents.length}` : undefined}
            action={!isReadOnly && group.templates.length > 0 ? (
              <span className="pill-row">
                {group.templates.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className="pill-btn pill-btn--primary pill-btn--sm"
                    disabled={creatingTemplateId !== null}
                    onClick={() => startCreate(t)}
                  >
                    <PlusIcon size={12} />
                    {creatingTemplateId === t.id
                      ? 'Creating…'
                      : group.templates.length === 1 ? 'New' : `New ${t.name}`}
                  </button>
                ))}
              </span>
            ) : undefined}
          />

          {group.documents.length === 0 ? (
            <p className="ui-table__empty">No documents yet.</p>
          ) : (
            <div className="ui-cards">
              {group.documents.map(doc => {
                const author = documentAuthor(doc);
                return (
                  <div
                    key={doc.id}
                    className="ui-card ui-card--link"
                    role="button"
                    tabIndex={0}
                    onClick={() => openDocument(doc.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDocument(doc.id);
                      }
                    }}
                  >
                    <span
                      className="ui-card__avatar"
                      title={author ? `${author.first_name} ${author.last_name}` : 'No signatory yet'}
                      aria-hidden="true"
                    >
                      {initials(author)}
                    </span>
                    <div className="ui-card__main">
                      <div className="ui-card__top">
                        {/* The group heading already names the type, so the
                            template name is not repeated here. */}
                        <span className="ui-card__title">{documentLabel(doc)}</span>
                      </div>
                      <div className="ui-card__meta">
                        <span>{shortDate(doc.document_date)}</span>
                        {author && <span>{author.first_name} {author.last_name}</span>}
                      </div>
                    </div>
                    <div className="ui-card__side">
                      {/* No "Prepared by ✓": the status says whether it is signed. */}
                      <StatusPill label={statusLabel[doc.status]} tone={statusTone[doc.status] ?? 'neutral'} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DocumentsTab;
